//Modules
import Image from "next/image";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";

//Context
import { useAuth } from "../lib/hooks/auth";

//Components
import Layout from "../components/shared/layout/external";
import LegacyPrompt from "../components/features/legacy/legacyPrompt";
import { useState } from "react";

//Validation
const schema = yup
  .object({
    firstName: yup.string().required("Please enter a first name"),
    lastName: yup.string().required("Please enter a last name"),
    company: yup.string().required("Please enter your organization name"),
    phone: yup
      .string()
      .required("Please enter a phone number")
      .matches(/^[+\-\)\(\d]+$/, "Must be a valid phone number")
      .min(8, "Minimum length 8")
      .max(20, "Maximum length 11"),
    email: yup
      .string()
      .email("Please enter a valid email")
      .required("Please enter your email"),
    password: yup
      .string()
      .required("Please enter a password")
      .min(8, "must be longer than 8 characters"),
    confirmPassword: yup
      .string()
      .required("Please confirm your password")
      .oneOf([yup.ref("password"), null], "Passwords must match"),
    recaptcha: yup.string().required("Please complete recaptcha"),
  })
  .required();

export default function SignUp() {
  const [methodUser, setMethodUser] = useState(null);
  const { signup, checkIfMethodUserExists, loadingAuth, setLoadingAuth } =
    useAuth();
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  async function onSubmit(values) {
    setLoadingAuth(true);
    // Check if we already have this user in method.
    // If we do, prompt them to perform legacy sign up,
    // Otherwise sign up normally.
    let user = await checkIfMethodUserExists(values.email);
    if (user.exists) {
      setMethodUser(user);
      setLoadingAuth(false);
    } else if (user.error) {
      setLoadingAuth(false);
    } else {
      signup(values);
    }
  }

  async function handleReCAPTCHA(token) {
    let result = await axios({
      method: "post",
      url: "/api/recaptcha",
      headers: {
        "Content-Type": "application/json",
        Authorization: "ZxbqflYv9TgNec5",
      },
      data: token,
    }).catch((error) => console.log(error));

    // fill the recaptcha field if recaptcha successful so form can submit
    // (yup schema stops it submitting if there is no value present)
    if (result.data.success) {
      setValue("recaptcha", "success");
    }
    // If recaptcha fails, setError so form can't submit.
    else {
      setError("recaptcha", {
        type: "manual",
        message: "ReCAPTCHA failed, please try again",
      });
    }
  }

  return (
    <Layout>
      {methodUser?.exists ? (
        <LegacyPrompt user={methodUser} />
      ) : (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div>
              <Link href="/">
                <div className="flex justify-center cursor-pointer">
                  <Image
                    src="/logo/wave-distro-logo.png"
                    alt="Wave Distribution Logo"
                    layout="intrinsic"
                    width={230}
                    height={66}
                  />
                </div>
              </Link>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                Create an account
              </h2>
              <p className="mt-4 text-center text-sm text-gray-600">
                <Link href="/legacy">
                  <span className="font-medium text-primaryDark hover:text-primary">
                    Click here if you have an account on our previous website
                  </span>
                </Link>
              </p>
            </div>
            <form
              className="mt-8 space-y-6"
              onSubmit={handleSubmit((data) => onSubmit(data))}
            >
              <div className="relative space-y-2">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    First Name
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      {...register("firstName")}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {errors.firstName && (
                    <div className="flex items-center pointer-events-none mt-2 text-red-600">
                      <svg
                        className="h-5 w-5 text-red-500 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.firstName.message}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Last Name
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      {...register("lastName")}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {errors.lastName && (
                    <div className="flex items-center pointer-events-none mt-2 text-red-600">
                      <svg
                        className="h-5 w-5 text-red-500 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.lastName.message}
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Company
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="company"
                      name="company"
                      type="text"
                      autoComplete="organization"
                      {...register("company")}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {errors.company && (
                    <div className="flex items-center pointer-events-none mt-2 text-red-600">
                      <svg
                        className="h-5 w-5 text-red-500 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.company.message}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Contact Number
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="phone"
                      name="phone"
                      type="text"
                      autoComplete="tel"
                      {...register("phone")}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {errors.phone && (
                    <div className="flex items-center pointer-events-none mt-2 text-red-600">
                      <svg
                        className="h-5 w-5 text-red-500 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.phone.message}
                    </div>
                  )}
                  {!errors.phone && (
                    <p
                      className="mt-1 text-sm text-gray-500 flex items-baseline"
                      id="phone-information"
                    >
                      <svg
                        className="self-center flex-shrink-0 h-4 w-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Please include country code
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="email"
                      name="email"
                      type="text"
                      autoComplete="email"
                      {...register("email")}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {errors.email && (
                    <div className="flex items-center pointer-events-none mt-2 text-red-600">
                      <svg
                        className="h-5 w-5 text-red-500 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.email.message}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      {...register("password")}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {errors.password && (
                    <div className="flex items-center pointer-events-none mt-2 text-red-600">
                      <svg
                        className="h-5 w-5 text-red-500 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.password?.message}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Confirm Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      {...register("confirmPassword")}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <div className="flex items-center pointer-events-none mt-2 text-red-600">
                      <svg
                        className="h-5 w-5 text-red-500 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.confirmPassword?.message}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div>
                  <ReCAPTCHA
                    {...register("recaptcha")}
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA}
                    onChange={handleReCAPTCHA}
                  />
                </div>
                {errors.recaptcha && (
                  <div className="flex items-center pointer-events-none mt-2 text-red-600">
                    <svg
                      className="h-5 w-5 text-red-500 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.recaptcha?.message}
                  </div>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    {/* <!-- Heroicon name: solid/lock-closed --> */}
                    <svg
                      className="h-5 w-5 text-primaryDark group-hover:text-primary"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  {loadingAuth
                    ? "Loading..."
                    : // <Loader height="5" width="5" color="white" />
                      "Sign Up"}
                </button>
              </div>
            </form>
          </div>
          {/* Modals */}
          {/* <Reset open={reset} closeFunction={setReset} />
      <Alert type={error && error.type} alert={error} /> */}
        </div>
      )}
    </Layout>
  );
}
